import { useEffect, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { TextField } from '../../ui/TextField';
import { TextEditor } from '../../ui/TextEditor';
import { PromptChip } from '../../ui/PromptChip';
import { shake } from '../../ui/motion';
import { PhotoImprove } from '../photo/PhotoImprove';
import { applyStudioChip, studioChipOn, type StudioChipMode } from './studio-chips';

export type { StudioChipMode } from './studio-chips';
/** Один BotCore.box/editor для фото и видео. Экран передаёт только продуктовые подписи и политику API. */
export function StudioPromptField({
  label,
  value,
  onChange,
  minimum,
  limit,
  disabled,
  presets,
  improvement,
  kind,
  chipMode = 'set',
  note = 'Без политики и запрещённых тем',
  placeholder = 'Опишите, что хотите увидеть',
  hint,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  minimum: number;
  limit: number;
  disabled: boolean;
  presets: ReadonlyArray<readonly [string, string]>;
  improvement: boolean;
  kind: 'photo' | 'video';
  /** Чипы-добавки дописывают текст, готовые промпты заменяют его */
  chipMode?: StudioChipMode;
  /** Подпись-правило под полем (пусто — без подписи) */
  note?: string;
  placeholder?: string;
  /** «необязательно» в подписи текстовой секции листа */
  hint?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const chips = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!overflow) return;
    const timer = window.setTimeout(() => setOverflow(false), 2000);
    return () => clearTimeout(timer);
  }, [overflow]);
  const press = (prompt: string, element: HTMLElement | null) => {
    const next = applyStudioChip(value, prompt, chipMode);
    if (Array.from(next).length > limit) {
      shake(element);
      setOverflow(true);
      return;
    }
    onChange(next);
  };
  const scenes = (editor = false) =>
    !!presets.length && (
      <div ref={editor ? undefined : chips} className={`chips${editor ? ' bc-edch' : ''}`}>
        {presets.map(([title, prompt]) => (
          <PromptChip
            key={title}
            label={uiText(title)}
            selected={studioChipOn(value, prompt, chipMode)}
            disabled={disabled}
            onClick={event => press(prompt, (event?.currentTarget as HTMLElement | undefined) ?? null)}
          />
        ))}
      </div>
    );
  const improve = improvement && <PhotoImprove kind={kind} text={value} disabled={disabled} onPick={onChange} />;
  return (
    <div className="photo-field pf-ta">
      <TextField
        label={hint ? `${label} · ${uiText(hint)}` : label}
        value={value}
        onChange={onChange}
        minimum={minimum}
        limit={limit}
        disabled={disabled}
        onExpand={() => setExpanded(true)}
        expandLabel={uiText('Во весь экран')}
        placeholder={uiText(placeholder)}
      >
        {scenes()}
        <div className="foot studio-description-actions">
          {!expanded && improve}
          {note && <span className="pf-fn">{uiText(note)}</span>}
        </div>
        {overflow && (
          <p className="studio-settings-tip" role="status">
            {uiFormat('Не помещается: до {0} знаков', limit)}
          </p>
        )}
      </TextField>
      {expanded && (
        <TextEditor
          label={uiFormat('{0} во весь экран', label)}
          value={value}
          onChange={onChange}
          limit={limit}
          disabled={disabled}
          onClose={() => setExpanded(false)}
          toolbar={
            <>
              {improve}
              {scenes(true)}
            </>
          }
        />
      )}
    </div>
  );
}
